<template>
  <el-drawer
    class="drawer-inner-scroller"
    :model-value="visible"
    destroy-on-close
    size="486"
    @close="emit('close')"
  >
    <template #header>
      <div class="my-header">
        <h4 class="pb-3 text-xl text-[#344767] font-semibold">
          {{ bindModel ? '子企业绑定集团' : '可绑定企业信息' }}
        </h4>
      </div>
    </template>

    <div v-if="edit">
      <div class="text-[#6b7891] text-sm pb-[8px]">
        搜索绑定企业
      </div>

      <el-input
        :model-value="creditCode"
        class="mb-[16px]"
        :value="creditCode"
        size="large"
        clearable
        placeholder="输入统一社会信用代码"
        @input="onChange"
        @keyup.enter="fetchCompanyInfo"
      >
        <template #prefix>
          <svg-icon class="!w-[20px]" icon="iconamoon:search-thin" />
        </template>
        <template #suffix>
          <el-button link type="primary" :loading="loading" @click="fetchCompanyInfo">
            查询
          </el-button>
        </template>
      </el-input>
    </div>

    <div v-loading="loading">
      <template v-if="edit">
        <div v-if="!emptyData">
          <div class="text-sm text-[#6B7891] py-[8px]">
            可绑定企业信息
          </div>

          <div class="bg-[#F5F7FA] rounded-sm h-[160px] p-4 text-sm text-[#6B7891] mb-[16px]">
            <div class="flex pb-[14px]">
              <span class="block w-[125px]">企业名称</span>
              <span>{{ info.companyName }}</span>
            </div>
            <div class="flex pb-[14px]">
              <span class="block w-[125px]">统一社会信用代码</span>
              <span>{{ info.creditCode }}</span>
            </div>
            <div class="flex pb-[14px]">
              <span class="block w-[125px]">联系人姓名</span>
              <span>{{ info.contactName }}</span>
            </div>
            <div class="flex">
              <span class="block w-[125px]">联系人电话</span>
              <span>{{ info.contactPhone }}</span>
            </div>
          </div>

          <div class="pb-3">
            <div class="text-[#6b7891] text-sm pb-[8px]">
              {{ bindModel ? '申请' : '邀请' }}说明
            </div>

            <div class="flex relative">
              <el-input
                v-model="remark"
                type="textarea"
                :rows="5"
                clearable
                resize="none"
                autocomplete="nony"
                placeholder="请输入"
                maxlength="500"
                show-word-limit
                @keydown="(e: KeyboardEvent | Event) => {
                  if ((e as KeyboardEvent).keyCode === 13) {
                    e.preventDefault();
                    onConfirm();
                  }
                }"
              />
            </div>
          </div>

          <div class="drawer-btns">
            <el-button type="primary" :loading="saving" @click="onConfirm">
              {{ bindModel ? '申请' : '邀请' }}
            </el-button>
          </div>
        </div>
      </template>

      <!-- 详情模式 -->
      <template v-else>
        <div class="pb-3">
          <div class="text-[#6b7891] text-sm pb-[8px]">
            集团名称
          </div>

          <div class="flex relative">
            <el-input :model-value="data.companyGroupName" disabled />
          </div>
        </div>
        <div class="pb-3">
          <div class="text-[#6b7891] text-sm pb-[8px]">
            集团团统一社会信用代码
          </div>

          <div class="flex relative">
            <el-input :model-value="data.groupCreditCode" disabled />
          </div>
        </div>
        <div class="pb-3">
          <div class="text-[#6b7891] text-sm pb-[8px]">
            {{ bindModel ? '申请' : '邀请' }}说明
          </div>

          <div class="flex relative">
            <el-input
              :model-value="data.remark"
              type="textarea"
              :rows="5"
              disabled
              resize="none"
            />
          </div>
        </div>
      </template>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { queryCompanyByCreditCode, queryGroupsByCreditCode } from '../../api/app-account/group';
import { IGroupCompany } from '../../types/app-account/group';
import { ICompanyGroup } from '../../types/common';
import { SvgIcon } from '@vue3/components';
import { ElMessage } from 'element-plus';
import { computed, PropType, ref, toRaw, watchEffect } from 'vue';

interface ICompanyMeta extends IGroupCompany {remark?: string}

let lock = false;

defineOptions({ name: 'InviteCompany' });

const emit = defineEmits<{
  close: [];
  confirm: [value: ICompanyMeta];
}>();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  edit: {
    type: Boolean,
    default: false,
  },
  isSubCompany: {
    type: Boolean,
    default: false,
  },
  bindModel: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<ICompanyGroup>,
    default() {
      return {} as ICompanyGroup;
    },
  },
});

const creditCode = ref('');
const loading = ref(false);

const info = ref({} as ICompanyMeta);
const remark = ref('');
const emptyData = computed(() => !Object.keys(info.value).length);

const onConfirm = () => {
  emit('confirm', {
    ...toRaw(info.value),
    remark: remark.value,
  } as ICompanyMeta);
};

const onChange = (val: string) => {
  creditCode.value = val;

  if (!val) {
    resetInfo();
  }
};

const resetInfo = () => {
  remark.value = '';
  info.value = {} as ICompanyMeta;
  lock = false;
};

const fetchCompanyInfo = async () => {
  if (lock) return;

  if (!creditCode.value) {
    ElMessage.error('请输入统一社会信用代码');
    return;
  }

  lock = true;
  loading.value = true;
  remark.value = '';

  try {
    if (!props.isSubCompany) {
      info.value = await queryCompanyByCreditCode(creditCode.value);
    } else {
      info.value = await queryGroupsByCreditCode(creditCode.value);
    }

    if (!info.value?.companyName) {
      ElMessage.error('未查询到该企业信息');
      info.value = {} as ICompanyMeta;
    }
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
    resetInfo();
  }

  loading.value = false;
  setTimeout(() => {
    lock = false;
  }, 800);
};

watchEffect(() => {
  if (props.visible) {
    loading.value = false;
    info.value = {} as ICompanyMeta;
    creditCode.value = '';
    remark.value = '';
    lock = false;
  }
});
</script>
